import type { RouteLocationRaw } from 'vue-router';
import { createMyBellToast } from '@/api/notificationApi';
import { useAppShellStore } from '@/stores/useAppShellStore';
import { pageTabRouteKey } from '@/utils/pageTabRouteKey';

export type PageTabCompletionPayload = {
  tabKey: string;
  title: string;
  content?: string;
  actionUrl?: string;
  mergeKey?: string;
  type?: string;
};

export function routeToTabKey(to: RouteLocationRaw): string {
  return pageTabRouteKey(to);
}

export function projectDashboardTabKey(projectId: number | string): string {
  return `ProjectDashboard:${projectId}`;
}

export function projectRequirementsTabKey(projectId: number | string): string {
  return routeToTabKey({
    name: 'ProjectRequirements',
    params: { id: String(projectId) },
    query: {},
  });
}

/** 将通知跳转链接映射到可能承载该任务的页签 key（项目需求 / WBS / 文档均落在项目 Dashboard 页签） */
export function tabKeysForActionUrl(actionUrl?: string | null): string[] {
  const url = (actionUrl || '').trim();
  if (!url) return [];
  const m = url.match(/^\/project\/(\d+)(?:\/(requirements|dashboard|wbs|documents(?:\/[^/?#]+)?))?\/?/);
  if (!m) return [url.startsWith('/') ? url : `/${url}`];
  const pid = m[1];
  return [projectDashboardTabKey(pid), projectRequirementsTabKey(pid)];
}

export function actionUrlForTabKey(tabKey: string): string | undefined {
  if (tabKey.startsWith('ProjectDashboard:')) {
    const pid = tabKey.slice('ProjectDashboard:'.length);
    return pid ? `/project/${pid}/dashboard` : undefined;
  }
  try {
    const parsed = JSON.parse(tabKey) as { name?: string; params?: Record<string, unknown>; query?: Record<string, unknown> };
    const name = parsed.name || '';
    const pid = String(parsed.params?.id ?? '');
    if (name === 'ProjectRequirements' && pid) return `/project/${pid}/requirements`;
    if (name === 'ProjectWbsSnapshot' && pid) return `/project/${pid}/dashboard?innerTab=wbs`;
    if (name === 'ProjectDocumentViewer' && pid) {
      const objectKey = String(parsed.params?.objectKey ?? '');
      if (objectKey) {
        return `/project/${pid}/dashboard?innerTab=doc&objectKey=${encodeURIComponent(objectKey)}`;
      }
      return `/project/${pid}/dashboard`;
    }
    if (name === 'ResourceCreate') return '/resources/create';
    if (name === 'Resources') return '/resources';
    if (name === 'ProjectCreate') {
      const draft = String(parsed.query?.draft ?? '');
      return draft ? `/project/create?draft=${encodeURIComponent(draft)}` : '/project/create';
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

/** 登记页签内运行中任务；返回 end 函数，在 finally 中调用 */
export function trackPageTabRunningTask(tabKey: string): () => void {
  if (!tabKey) return () => {};
  const shell = useAppShellStore();
  shell.incrementTabRunningTasks(tabKey);
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    shell.decrementTabRunningTasks(tabKey);
  };
}

export function findOpenTabForKeys(keys: string[]): { tabKey: string; isActive: boolean } | null {
  const shell = useAppShellStore();
  for (const key of keys) {
    if (!key) continue;
    const tab = shell.pageTabs.find((item) => item.key === key);
    if (tab) {
      return { tabKey: key, isActive: shell.activePageTabKey === key };
    }
  }
  return null;
}

/**
 * 页签仍打开：非激活页签显示实心圆点，不弹铃铛。
 * 页签已关闭：写入铃铛通知。
 */
export async function notifyPageTabCompletionOrBell(payload: PageTabCompletionPayload): Promise<void> {
  const shell = useAppShellStore();
  const candidateKeys = [payload.tabKey, ...tabKeysForActionUrl(payload.actionUrl)].filter(Boolean);
  const open = findOpenTabForKeys(candidateKeys);

  if (open) {
    if (!open.isActive) {
      shell.markTabCompletion(open.tabKey);
    }
    return;
  }

  try {
    await createMyBellToast({
      title: payload.title,
      content: payload.content,
      actionUrl: payload.actionUrl,
      mergeKey: payload.mergeKey,
      type: payload.type ?? 'AI_TASK_COMPLETED',
    });
  } catch {
    /* ignore */
  }
}

/** 铃铛待展示列表：若对应页签仍打开则转为页签圆点并标记已读 */
export async function rerouteBellToastToTabDot(
  item: { id: number; actionUrl?: string | null; mergeKey?: string | null; title?: string | null },
  markRead: (id: number) => Promise<void>,
): Promise<boolean> {
  const keys = tabKeysForActionUrl(item.actionUrl);
  const merge = (item.mergeKey || '').trim();
  if (merge.startsWith('AI_COMPONENT_RECOMMEND:') || merge.startsWith('AI_DRAWING_REVIEW:')) {
    const pid = merge.split(':').pop();
    if (pid) {
      keys.unshift(projectDashboardTabKey(pid), projectRequirementsTabKey(pid));
    }
  }
  const open = findOpenTabForKeys(keys);
  if (!open) return false;

  if (!open.isActive) {
    useAppShellStore().markTabCompletion(open.tabKey);
  }
  try {
    await markRead(item.id);
  } catch {
    /* ignore */
  }
  return true;
}
